import React, { useEffect } from 'react'
import Table from 'core-system/Table'
import { TableRowCell, TableRow } from 'core-system/Table'
import Text from 'core-system/Text'
import { AppState } from 'redux/config/store'
import { useSelector, useDispatch } from 'react-redux'
import { ProgramsType } from '../Shared/types'
import launchProgramsWordings from '../Shared/wordings.json'
import styled from 'styled-components'
import { useNavigate, useLocation } from 'react-router-dom'
import { Locations } from 'shared/Router/Locations'
import { programSuccessActions } from './ProgramSuccessActions'
import { SegmentList, findProgramIdForSegment } from '../Shared/helperFunctions'
import { CircularProgress } from '@mui/material'
import palette from 'core-system/Themes/palette'
import { v4 as uuidv4 } from 'uuid'
import { values } from 'lodash'
import Chip from 'core-system/Chip'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SetupCompleteBox from './Components/SetupCompleteBox'
import ProgramDetails from './Components/ProgramDetails'
import moment from 'moment'
import NeedAssistanceBox from './Components/NeedAssistanceBox'

const SuccessStatus = () => {
  return <Chip variant='green'>Success</Chip>
}

const ErrorStatus = () => {
  return <Chip variant='red'>Error</Chip>
}

const ContactContainer = styled.div`
  margin-top: 2rem;
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const ProgramSuccessView = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()

  const isFromUpdate = location.state?.fromUpdate
  const programIds = location.state?.programIds
  const launchId = location.state?.launchId || uuidv4()

  const initialSelectedSegments = location.state?.initialSelectedSegments || []
  const {
    selectedPrograms,
    selectedSegments,
    selectedDate,
    programName,
    currency,
  } = useSelector((state: AppState) => state.launchPrograms)
  const { qtf, grh, micromobility, flex, transitPass, coupon, reportedUsage } =
    useSelector((state: AppState) => state.programEditing)
  const { programEditSuccess, programEditError } = useSelector(
    (state: AppState) => state.programSuccess
  )
  const merchantsMap = useSelector(
    (state: AppState) => state.programs.merchantsMap
  )
  const currentUser = useSelector((state: AppState) => state.user.profile)
  const allQtfPrograms = useSelector((state: AppState) => state.qtf.qtfPrograms)

  useEffect(() => {
    // Filter out transit-pass to check if there are any regular programs selected
    const regularPrograms = selectedPrograms.filter(
      (p): p is ProgramsType => p !== 'transit-pass' && p !== null
    )
    if (
      regularPrograms.length === 0 &&
      !selectedPrograms.includes('transit-pass')
    ) {
      navigate(Locations.Programs.Overview)
    }
  }, [])

  // Launching Programs
  useEffect(() => {
    // Filter out transit-pass as it doesn't need to be launched separately
    const regularPrograms = selectedPrograms.filter(
      (p): p is ProgramsType => p !== 'transit-pass' && p !== null
    )
    if (regularPrograms.length > 0) {
      regularPrograms.forEach((program: ProgramsType) => {
        switch (program) {
          case 'qtf':
            editQtfProgramForAllSegments(launchId)
            break
          case 'grh':
            editGrhProgramForAllSegments(launchId, program)
            break
          case 'micromobility':
            editMicromobilityProgramForAllSegments(launchId, program)
            break
          case 'flex':
            editFlexProgramForAllSegments(launchId, program)
            break
          case 'transitPass':
            editTransitPassProgramForAllSegments(launchId, program)
            break
          case 'coupon':
            editCouponProgramForAllSegments(launchId, program)
            break
          case 'reportedUsage':
            editReportedUsageProgramForAllSegments(launchId, program)
            break
        }
      })
    }
  }, [selectedPrograms])

  // Getting programs to cancel if a segment is removed
  const getProgramsToCancel = () => {
    const programsToCancel = []
    initialSelectedSegments.forEach((segment: SegmentList) => {
      if (!selectedSegments.find((s: SegmentList) => s.id === segment.id)) {
        programsToCancel.push(segment)
      }
    })
    return programsToCancel
  }

  // get qtf type map
  const getQtfTypeMap = (segment: SegmentList) => {
    const program =
      allQtfPrograms[segment.id].active || allQtfPrograms[segment.id].nextMonth
    const typeMap = program?.typeMap || {}
    return typeMap
  }

  const editQtfProgramForAllSegments = async (launchId: string) => {
    if (isFromUpdate) {
      // Cancelling programs if a segment is removed
      const programsToCancel = getProgramsToCancel()
      programsToCancel.forEach((segment: SegmentList) => {
        const typeMap = getQtfTypeMap(segment)
        const programIds = values(typeMap).map((type) => type.id)
        dispatch(
          programSuccessActions.cancelQtfProgramForASegment({
            programIds,
          })
        )
      })
      // updating the remaining programs
      selectedSegments.forEach((segment: SegmentList) => {
        // Getting typeMap from qtf because there are two types of qtf programs and
        // they have different program ids
        const typeMap = getQtfTypeMap(segment)
        dispatch(
          programSuccessActions.updateQtfProgramForASegment({
            qtf,
            segment,
            selectedDate,
            currentUserId: currentUser.id,
            typeMap,
            launchId,
            superProgramName: programName,
          })
        )
      })
      return
    }
    dispatch(
      programSuccessActions.createBulkQtfProgramForASegmentList({
        qtf,
        segmentIdList: selectedSegments.map(
          (segment: SegmentList) => segment.id
        ),
        selectedDate,
        currentUserId: currentUser.id,
        launchId,
        superProgramName: programName,
      })
    )
  }

  const editGrhProgramForAllSegments = async (
    launchId: string,
    program: ProgramsType
  ) => {
    if (isFromUpdate) {
      const ids = programIds[program]
      // Cancelling programs if a segment is removed
      const programsToCancel = getProgramsToCancel()
      programsToCancel.forEach((segment: SegmentList) => {
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.cancelGrhProgramForASegment({
            programId,
          })
        )
      })
      // updating the remaining programs
      selectedSegments.forEach((segment: SegmentList) => {
        // finding the program id for the segment
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.updateGrhProgramForASegment({
            grh,
            segment,
            selectedDate,
            merchantsMap,
            programId,
            launchId,
            superProgramName: programName,
            currency,
          })
        )
      })
      return
    }
    // If it is a new program, we need to create the program
    selectedSegments.forEach((segment: SegmentList) => {
      dispatch(
        programSuccessActions.createGrhProgramForASegment({
          grh,
          segment,
          selectedDate,
          merchantsMap,
          launchId,
          superProgramName: programName,
          currency,
        })
      )
    })
  }

  const editMicromobilityProgramForAllSegments = async (
    launchId: string,
    program: ProgramsType
  ) => {
    if (isFromUpdate) {
      const ids = programIds[program]
      // Cancelling programs if a segment is removed
      const programsToCancel = getProgramsToCancel()
      programsToCancel.forEach((segment: SegmentList) => {
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.cancelMicromobilityProgramForASegment({
            programId,
          })
        )
      })
      // updating the remaining programs
      selectedSegments.forEach((segment: SegmentList) => {
        // finding the program id for the segment
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.updateMicromobilityProgramForASegment({
            micromobility,
            segment,
            selectedDate,
            merchantsMap,
            programId,
            launchId,
            superProgramName: programName,
            currency,
          })
        )
      })
      return
    }
    // If it is a new program, we need to create the program
    selectedSegments.forEach((segment: SegmentList) => {
      dispatch(
        programSuccessActions.createMicromobilityProgramForASegment({
          micromobility,
          segment,
          selectedDate,
          merchantsMap,
          launchId,
          superProgramName: programName,
          currency,
        })
      )
    })
  }

  const editFlexProgramForAllSegments = async (
    launchId: string,
    program: ProgramsType
  ) => {
    if (isFromUpdate) {
      const ids = programIds[program]
      // Cancelling programs if a segment is removed
      const programsToCancel = getProgramsToCancel()
      programsToCancel.forEach((segment: SegmentList) => {
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.cancelFlexProgramForASegment({
            programId,
          })
        )
      })
      // updating the remaining program
      selectedSegments.forEach((segment: SegmentList) => {
        // finding the program id for the segment
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.updateFlexProgramForASegment({
            flex,
            segment,
            merchantsMap,
            selectedDate,
            programId,
            launchId,
            superProgramName: programName,
            currency,
          })
        )
      })
      return
    }
    // If it is a new program, we need to create the program
    selectedSegments.forEach((segment: SegmentList) => {
      dispatch(
        programSuccessActions.createFlexProgramForASegment({
          flex,
          segment,
          selectedDate,
          merchantsMap,
          launchId,
          superProgramName: programName,
          currency,
        })
      )
    })
  }

  const editTransitPassProgramForAllSegments = async (
    launchId: string,
    program: ProgramsType
  ) => {
    if (isFromUpdate) {
      const ids = programIds[program]
      const programsToCancel = getProgramsToCancel()
      programsToCancel.forEach((segment: SegmentList) => {
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.cancelTransitPassProgramForASegment({
            programId,
          })
        )
      })
      selectedSegments.forEach((segment: SegmentList) => {
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.updateTransitPassProgramForASegment({
            transitPass,
            segment,
            selectedDate,
            programId,
            launchId,
            superProgramName: programName,
            description,
            currency,
          })
        )
      })
      return
    }
    selectedSegments.forEach((segment: SegmentList) => {
      dispatch(
        programSuccessActions.createTransitPassProgramForASegment({
          transitPass,
          segment,
          selectedDate,
          launchId,
          superProgramName: programName,
          description,
          currency,
        })
      )
    })
  }

  const editCouponProgramForAllSegments = async (
    launchId: string,
    program: ProgramsType
  ) => {
    if (isFromUpdate) {
      const ids = programIds[program]
      const programsToCancel = getProgramsToCancel()
      programsToCancel.forEach((segment: SegmentList) => {
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.cancelCouponProgramForASegment({
            programId,
          })
        )
      })
      selectedSegments.forEach((segment: SegmentList) => {
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.updateCouponProgramForASegment({
            coupon,
            segment,
            selectedDate,
            programId,
            launchId,
            superProgramName: programName,
            description,
            currency,
          })
        )
      })
      return
    }
    selectedSegments.forEach((segment: SegmentList) => {
      dispatch(
        programSuccessActions.createCouponProgramForASegment({
          coupon,
          segment,
          selectedDate,
          launchId,
          superProgramName: programName,
          description,
          currency,
        })
      )
    })
  }

  const editReportedUsageProgramForAllSegments = async (
    launchId: string,
    program: ProgramsType
  ) => {
    if (isFromUpdate) {
      const ids = programIds[program]
      const programsToCancel = getProgramsToCancel()
      programsToCancel.forEach((segment: SegmentList) => {
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.cancelReportedUsageProgramForASegment({
            programId,
          })
        )
      })
      selectedSegments.forEach((segment: SegmentList) => {
        const programId = findProgramIdForSegment(ids, segment)
        dispatch(
          programSuccessActions.updateReportedUsageProgramForASegment({
            reportedUsage,
            segment,
            selectedDate,
            programId,
            launchId,
            superProgramName: programName,
            description,
            currency,
          })
        )
      })
      return
    }
    selectedSegments.forEach((segment: SegmentList) => {
      dispatch(
        programSuccessActions.createReportedUsageProgramForASegment({
          reportedUsage,
          segment,
          selectedDate,
          launchId,
          superProgramName: programName,
          description,
          currency,
        })
      )
    })
  }

  const renderStatus = (program: ProgramsType) => {
    if (programEditSuccess[program].length >= selectedSegments.length) {
      return <SuccessStatus />
    }
    if (programEditError[program].length > 0) {
      return <ErrorStatus />
    }
    return (
      <CircularProgress
        size={20}
        style={{ color: palette.secondary.purple500 }}
      />
    )
  }

  const hasAnyError = Object.values(programEditError).some(
    (error) => error.length > 0
  )

  const HeaderCell = (text: string, align: 'center' | 'left' = 'center') => (
    <TableRowCell align={align}>
      <Text fontWeight={600} variant={'action3'}>
        {text}
      </Text>
    </TableRowCell>
  )

  const headerCells = [
    HeaderCell('Programs', 'left'),
    HeaderCell('Status', 'center'),
    HeaderCell('Groups', 'center'),
  ]

  const ProgramRow = ({ program }: { program: ProgramsType }) => {
    return (
      <TableRow>
        <TableRowCell align='left'>
          <Text variant='action4'>{launchProgramsWordings[program].title}</Text>
        </TableRowCell>
        <TableRowCell align='center'>{renderStatus(program)}</TableRowCell>
        <TableRowCell align='center'>
          <Text variant='action3'>
            {programEditSuccess[program].length}/{selectedSegments.length}{' '}
            {isFromUpdate ? 'Updated' : 'Launched'}
          </Text>
        </TableRowCell>
      </TableRow>
    )
  }

  return (
    <>
      <Table
        headerCells={headerCells}
        rows={selectedPrograms.map((program: ProgramsType) => (
          <ProgramRow program={program} />
        ))}
      />
      {hasAnyError ? (
        <ContactContainer>
          <NeedAssistanceBox />
        </ContactContainer>
      ) : (
        <ContactContainer>
          <SetupCompleteBox />
          <ProgramDetails
            launchDate={moment(selectedDate.iso).format('MMM Do, YYYY')}
          />
          <NeedAssistanceBox />
          <ButtonsContainer>
            <PrimaryButton
              onClick={() => navigate(Locations.Programs.Overview)}
            >
              Go to Programs
            </PrimaryButton>
          </ButtonsContainer>
        </ContactContainer>
      )}
    </>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  ProgramSuccessView.displayName = 'ProgramSuccessView'
}

export default React.memo(ProgramSuccessView)
